import { eventPreviewView } from '../components/eventHelper';

const activeListeners = new WeakMap();

const newSessionId = () => window.crypto?.randomUUID?.()
    || `web_${Date.now()}_${Math.random().toString(36).slice(2)}`;

// Each attempt owns its preview identity and progress, even after another
// clip replaces the shared audio element.
export const createPreviewListening = ({
    audio,
    preview,
    send = eventPreviewView,
    getDuration = () => preview.totalTimeSeconds,
}) => {
    let session = null;
    const matchesSource = () => audio.src === preview.mp3URL;
    const capture = () => {
        if (session && matchesSource() && Number.isFinite(audio.currentTime)) {
            session.seconds = Math.max(session.seconds, Math.max(0, audio.currentTime));
        }
    };
    const save = (options = {}) => {
        send(preview.id, session.seconds, { sessionId: session.id, ...options });
    };
    const begin = () => {
        if (!session || session.finished) {
            const previous = activeListeners.get(audio);
            if (previous && previous !== listener) previous.finish('interrupted');
            session = { id: newSessionId(), seconds: 0, checkpoint: 0, finished: false };
        }
        activeListeners.set(audio, listener);
        capture();
        return true;
    };
    const start = () => {
        if (!matchesSource() || audio.paused || audio.ended) return false;
        return begin();
    };
    const checkpoint = () => {
        if (!matchesSource() || audio.paused || session?.finished) return;
        if (!session && !start()) return;
        capture();
        const checkpoint = Math.floor(session.seconds / 10);
        if (checkpoint > session.checkpoint) {
            session.checkpoint = checkpoint;
            save();
        }
    };
    const flush = () => {
        if (!session || session.finished) return;
        capture();
        save();
    };
    const finish = (reason) => {
        if (session?.finished) return;
        // A native ended event is itself evidence of playback, including a
        // clip shorter than the first checkpoint.
        if (!session && reason === 'completed' && matchesSource()) begin();
        if (!session || (reason === 'completed' && !matchesSource())) return;
        capture();
        if (reason === 'completed') {
            const finalDuration = Number.isFinite(audio.duration) && audio.duration > 0
                ? audio.duration : Number(getDuration());
            if (Number.isFinite(finalDuration)) session.seconds = Math.max(session.seconds, finalDuration);
        }
        session.finished = true;
        save(reason === 'completed' ? { completed: true }
            : reason === 'skipped' ? { skipped: true } : {});
        if (activeListeners.get(audio) === listener) activeListeners.delete(audio);
    };
    const reset = () => {
        finish('interrupted');
        session = null;
    };
    const listener = { start, checkpoint, flush, finish, reset };
    return listener;
};

export const finishActivePreviewListening = (audio, reason = 'interrupted') => {
    if (audio) activeListeners.get(audio)?.finish(reason);
};
